/**
 * Zod schemas for room entity types.
 *
 * These schemas mirror the TypeScript types in types.ts and gameSetup.types.ts,
 * enabling runtime validation of room entities.
 */
import { z } from 'zod'

// ─────────────────────────────────────────────────────────────────────────────
// Mate Schemas
// ─────────────────────────────────────────────────────────────────────────────

const mateSchema = z.object({
  /** Stable room-scoped mate id. */
  mateId: z.string(),
  /** Identifies which client owns this mate. */
  clientId: z.string(),
  /** Display name for the mate. */
  name: z.string(),
  /** Optional thumbnail image URL for the mate. */
  thumbnailUrl: z.string().optional(),
  /** Opaque, stable per-mate-slot identity used to remember a thumbnail across sessions. */
  persistentId: z.string().optional(),
  /** Controller device ID for shared-screen mates (e.g., 'X1', 'X2'). */
  controllerId: z.string().optional(),
  /** True if this mate is a bot. */
  isAI: z.boolean().optional(),
  /** Bot strategy (only relevant for bots). */
  botType: z.union([z.literal('mcts'), z.literal('random'), z.string()]).optional(),
  /** Bot configuration (only relevant for bots). */
  botConfig: z.record(z.string(), z.any()).optional(),
  /** True for a fake mate created by a client's Omni mode - see types.ts's Mate.isOmniMate. */
  isOmniMate: z.boolean().optional(),
})

const mateInRoomSchema = mateSchema.extend({
  /** ISO timestamp when this mate first joined the room. */
  joinedAt: z.string(),
})

// ─────────────────────────────────────────────────────────────────────────────
// Game Setup Schemas
// ─────────────────────────────────────────────────────────────────────────────

const gameSetupPhaseSchema = z.enum(['choosePlayerCount', 'assignSlots'])

const gameSetupSchema = z.object({
  /** Game being set up */
  gameId: z.string(),
  /** Current phase */
  phase: gameSetupPhaseSchema,
  /** Available player count options */
  slotCountOptions: z.array(z.number()),
  /** Chosen player count (null until chosen) */
  slotCount: z.number().nullable(),
  /** Mate assignments: mateId → slot index */
  assignments: z.record(z.string(), z.number()),
  /** Timestamp when setup started */
  startedAt: z.string(),
  /** Who initiated the setup */
  initiatorClientId: z.string(),
  /** Mates who may take a seat under the game's Host plays setting */
  seatEligibleMateIds: z.array(z.string()),
  /** Whether to randomize slot-to-player mapping when starting */
  randomize: z.boolean().optional(),
})

const setupReadinessSchema = z.object({
  /** Whether the game can be started */
  canStart: z.boolean(),
  /** Conditions that are missing or informational messages */
  missingConditions: z.array(z.string()),
})

// ─────────────────────────────────────────────────────────────────────────────
// Exported Schema Object
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Entity schemas for runtime validation.
 */
export const entitySchemas = {
  mate: mateSchema,
  mateInRoom: mateInRoomSchema,
  gameSetupPhase: gameSetupPhaseSchema,
  gameSetup: gameSetupSchema,
  setupReadiness: setupReadinessSchema,
} as const

// ─────────────────────────────────────────────────────────────────────────────
// Inferred Types
// ─────────────────────────────────────────────────────────────────────────────

export type Mate = z.infer<typeof mateSchema>
export type MateInRoom = z.infer<typeof mateInRoomSchema>
export type GameSetupPhase = z.infer<typeof gameSetupPhaseSchema>
export type GameSetup = z.infer<typeof gameSetupSchema>
export type SetupReadiness = z.infer<typeof setupReadinessSchema>
